import React, { type KeyboardEvent, useCallback, useDeferredValue, useEffect, useMemo, useRef } from 'react';

import Hotkeys from 'react-hot-keys';

import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import {
    addEmoji,
    copyEmoji,
    initSelectIndex,
    nextSearchEmoji,
    pasteEmoji,
    prevSearchEmoji,
    removeEmoji,
    setEmojiFilter,
    setEmojiList,
    setInputFocus,
    setInputValue,
    updateSearch,
    updateStatus,
} from '@app/store/actions/picker';
import { type Emoji } from '@app/store/types/picker';

import YatPickerFormLabel from './Label/Label';
import './PickerForm.scss';
import './PickerFormInput.scss';
import YatPickerSearchResult from './SearchResult/SearchResult';
import YatPickerSelectedEmojis from './SelectedEmojis/SelectedEmojis';
import './style.scss';

type HotkeyType = 'ctrl+c' | 'ctrl+v';

interface YatEmojiInputProps {
    placeholder?: string;
    minLength: number;
    maxLength: number;
}

const YatEmojiInput = ({ placeholder = '', minLength = 3, maxLength = 5 }: YatEmojiInputProps) => {
    const dispatch = useAppDispatch();

    const inputRef = useRef<HTMLInputElement>(null);
    const { currentIndex, currentIndexAffectsInput, inputValue, tiles, searchResult } = useAppSelector(
        (state) => state.picker,
    );
    const { wasm } = useAppSelector(({ emoji }) => emoji);
    const { editions } = useAppSelector((state) => state.editions);
    const deferredInputValue = useDeferredValue(inputValue);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            const selectionStart = (e.target as HTMLInputElement).selectionStart;
            const selectionEnd = (e.target as HTMLInputElement).selectionEnd;
            const caretIsFirstPosition = selectionEnd === 0;
            const caretIsLastPosition = selectionEnd === deferredInputValue.length;
            const currentIndexIsFirst = currentIndex === 0;
            const currentIndexIsLast = currentIndex + 1 === searchResult.length;

            // Remove last emoji on if caret is in position 0.
            if (e.key === 'Backspace' && caretIsFirstPosition) {
                dispatch(removeEmoji(tiles.length - 1));
            }

            // Reset search
            if (e.key === 'Escape') {
                dispatch(updateSearch(''));
            }

            if (!searchResult.length) return;

            // Add active/focused emoji.
            if (e.key === 'Enter') {
                dispatch(addEmoji(searchResult[currentIndex]));
            }

            // Handle arrow keys
            if (selectionStart === selectionEnd) {
                if (e.key === 'ArrowDown' || (e.key === 'ArrowRight' && caretIsLastPosition)) {
                    e.preventDefault();
                    dispatch(nextSearchEmoji());
                }
                if (e.key === 'ArrowUp' || (e.key === 'ArrowLeft' && caretIsLastPosition && !currentIndexIsFirst)) {
                    e.preventDefault();
                    dispatch(prevSearchEmoji());
                }
            }

            // handle tab key
            if (e.key === 'Tab' && !e.shiftKey && !currentIndexIsLast) {
                e.preventDefault();
                dispatch(nextSearchEmoji());
            }

            if (e.key === 'Tab' && e.shiftKey && !currentIndexIsFirst) {
                e.preventDefault();
                dispatch(prevSearchEmoji());
            }
        },
        [dispatch, searchResult, currentIndex, deferredInputValue, tiles],
    );

    const handleHotKeyDown = useCallback(
        (hotkey) => {
            const hk = hotkey as HotkeyType;
            if (hk === 'ctrl+c') {
                dispatch(copyEmoji());
            } else {
                dispatch(pasteEmoji());
            }
        },
        [dispatch],
    );

    // When search result currentIndex changes, change the input value with it.
    useEffect(() => {
        if (currentIndex && currentIndexAffectsInput && searchResult.length > 1) {
            dispatch(setInputValue(searchResult?.[currentIndex][1]));
        }
        // searchResult is excluded specifically as the search breaks by including in the dependencies
        // TODO Clean up this function to avoid the searchResult warning coming up.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, currentIndex, currentIndexAffectsInput]);

    const renderContent = useMemo(() => {
        return (
            <div className={`picker-form__inner${tiles.length > 0 ? ' picker-form__inner--has-tiles' : ''}`}>
                <div className="picker-form__selected-emojis">
                    <YatPickerSelectedEmojis />
                </div>
                <YatPickerFormLabel htmlFor="pickerFormInput" muted={searchResult.length > 0 || tiles.length > 0} />
                <div className="picker-form-input">
                    {/* {focused && <span className="yat-emoji__input--caret" />} */}
                    <div className="picker-form-input__spacer" aria-hidden="true">
                        {deferredInputValue ?? placeholder}
                    </div>
                    {/* <div className="picker-form-input__suggestion" aria-hidden="true">
                        {autocompleteText}
                    </div> */}
                    <input
                        id="pickerFormInput"
                        type="search"
                        ref={inputRef}
                        value={deferredInputValue}
                        placeholder={placeholder}
                        spellCheck="false"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        className={`picker-form-input__input${
                            tiles.length >= maxLength ? ' picker-form-input__input--hide-placeholder' : ''
                        }`}
                        role="combobox"
                        tabIndex={0}
                        aria-autocomplete="list"
                        aria-controls="yat-picker-search-result__list"
                        aria-expanded={searchResult.length ? 'true' : 'false'}
                        aria-activedescendant={`search-result-emoji-${currentIndex}`}
                        onChange={(e) => dispatch(updateSearch(e.target.value))}
                        onKeyDown={handleKeyDown}
                        onFocus={() => dispatch(setInputFocus(true))}
                        onBlur={() => dispatch(setInputFocus(false))}
                    />
                </div>
            </div>
        );
    }, [dispatch, handleKeyDown, tiles, deferredInputValue, placeholder, maxLength, currentIndex, searchResult.length]);

    const updateYatStatus = useCallback(
        (value) => {
            dispatch(updateStatus(value));
        },
        [dispatch],
    );

    useEffect(() => {
        const available = tiles?.length > 0 && tiles.length <= maxLength;
        if (available && !deferredInputValue) {
            updateYatStatus('YAT_DEFINED');
        }
    }, [tiles, deferredInputValue, minLength, maxLength, updateYatStatus]);

    useEffect(() => {
        if (wasm) {
            const filter = new wasm.CharacterSet(editions);
            const groupsList = filter.nonEmptyGroups();

            const availableEmojis: Emoji[] = [];

            groupsList.forEach((group) => {
                availableEmojis.push(...filter.filter(group, 'NameGroupSubgroup'));
            });

            dispatch(setEmojiList(availableEmojis));
            dispatch(setEmojiFilter(filter));
        }
    }, [wasm, dispatch, editions]);

    useEffect(() => {
        dispatch(initSelectIndex());
    }, [searchResult, dispatch]);

    return (
        <Hotkeys keyName="ctrl+v,ctrl+c" onKeyDown={handleHotKeyDown}>
            <div className="picker-form">
                {renderContent}
                <YatPickerSearchResult />
            </div>
        </Hotkeys>
    );
};

export default YatEmojiInput;
